/**
 * Inventory API client.
 * Calls relative `/api` endpoints with session cookies.
 */

import { apiFetch } from '../apiFetch'
import type {
  MaterialStockOverviewItem,
  MaterialStockDetailDto,
  StockMovementDto,
  StockMovementType,
  StockStatus,
  StockIssuePayload,
  SupplierReturnPayload,
  CustomerReturnPayload,
  ReworkOrderPayload,
  StatusTransferPayload,
  StockAdjustmentPayload,
  StockDocumentResponse,
  StockIssueDto,
  SupplierReturnDto,
  StockInboundDto,
  CreateStockInboundPayload,
  PatchStockInboundPayload,
} from './types'
import { getFreeStockList, type FreeStockItem } from '@/services/warehouse/freeStockApi'
import type { DocumentChangeLogDto } from '../procurement/types'

export type InventoryApiErrorResponse = {
  ok: false
  error: { code: string; message: string; status?: number }
}

export class InventoryApiClientError extends Error {
  readonly code: string
  readonly status: number

  constructor(code: string, message: string, status: number) {
    super(message)
    this.name = 'InventoryApiClientError'
    this.code = code
    this.status = status
  }
}

async function parseJson<T>(res: Response): Promise<T> {
  return (await res.json()) as T
}

function throwIfError(res: Response, body: unknown, fallbackMessage: string): void {
  if (res.ok) return
  const err = body as InventoryApiErrorResponse
  const code = err?.error?.code ?? 'HTTP_ERROR'
  const message = err?.error?.message ?? `${fallbackMessage} (${res.status})`
  const status = err?.error?.status ?? res.status
  throw new InventoryApiClientError(code, message, status)
}

function normalizeMovement(raw: any): StockMovementDto {
  const afterQty = raw.after_qty != null
    ? (typeof raw.after_qty === 'string' ? parseFloat(raw.after_qty) : Number(raw.after_qty))
    : undefined
  return {
    ...raw,
    qty_base: typeof raw.qty_base === 'string' ? parseFloat(raw.qty_base) : Number(raw.qty_base ?? 0),
    after_qty: afterQty,
    after: afterQty ?? (raw.after != null ? Number(raw.after) : undefined),
    is_free_stock: Boolean(raw.is_free_stock),
  }
}

export async function getStockOverview(options?: {
  below_threshold?: boolean
  archived?: boolean
}): Promise<MaterialStockOverviewItem[]> {
  const qs = options?.archived ? '?archived=true' : options?.below_threshold ? '?below_threshold=true' : ''
  const res = await apiFetch(`/api/stock${qs}`)
  const body = await parseJson<{ ok: boolean; data: MaterialStockOverviewItem[] } | InventoryApiErrorResponse>(res)
  throwIfError(res, body, 'Không thể tải tổng quan kho')
  const items = (body as { data: MaterialStockOverviewItem[] }).data
  return items.map((item) => ({
    ...item,
    id: item.material_id,
    material_name: item.name,
    base_uom: item.uom_default ?? '',
    on_hand: item.physical_available !== undefined ? Number(item.physical_available) : Number(item.available),
    available: Number(item.available),
    quarantine: Number(item.quarantine),
    blocked: Number(item.blocked),
    damaged: Number(item.damaged),
    rework: Number(item.rework),
    incoming_base: Number(item.incoming_base),
    physical_available: item.physical_available !== undefined ? Number(item.physical_available) : Number(item.available),
    reserved_base: item.reserved_base !== undefined ? Number(item.reserved_base) : 0,
    available_to_reserve: item.available_to_reserve !== undefined ? Number(item.available_to_reserve) : Number(item.available),
    min_qty: item.min_qty !== undefined ? Number(item.min_qty) : 0,
    safety_stock: item.safety_stock !== undefined ? Number(item.safety_stock) : 0,
    reorder_point: item.reorder_point !== undefined ? Number(item.reorder_point) : 0,
    max_qty: item.max_qty !== undefined && item.max_qty !== null ? Number(item.max_qty) : null,
    threshold: item.threshold !== undefined ? Number(item.threshold) : 0,
    replenish_policy: item.replenish_policy || 'MANUAL',
    below_threshold: Boolean(item.below_threshold),
    out_of_stock: Boolean(item.out_of_stock),
  }))
}

export async function getCombinedStockOverview(options?: { archived?: boolean }): Promise<MaterialStockOverviewItem[]> {
  // Danh sách lưu trữ chỉ có vật tư (hàng tự do không lưu trữ).
  if (options?.archived) return getStockOverview({ archived: true })
  const [materials, freeStock] = await Promise.all([getStockOverview(), getFreeStockList()])
  return [...materials, ...freeStock.map(toFreeStockOverview)]
}

function toFreeStockOverview(item: FreeStockItem): MaterialStockOverviewItem {
  return {
    material_id: `free:${item.id}`, free_stock_id: item.id, code: '', name: item.name,
    kind: 'FREE_STOCK', status: 'AVAILABLE', uom_default: item.uom,
    available: item.qty_on_hand, quarantine: 0, blocked: 0, damaged: 0, rework: 0,
    incoming_base: 0, incoming_unresolved: false, physical_available: item.qty_on_hand,
    reserved_base: 0, available_to_reserve: item.qty_on_hand, attributes: {}, threshold: 0,
    is_free_stock: true, free_stock_details: item.details, po_code: item.po_code,
  }
}

export async function getMaterialStock(materialId: string): Promise<MaterialStockDetailDto | null> {
  const res = await apiFetch(`/api/stock/${encodeURIComponent(materialId)}`)
  if (res.status === 404) return null
  const body = await parseJson<{ ok: boolean; data: any } | InventoryApiErrorResponse>(res)
  throwIfError(res, body, `Không thể tải tồn kho của vật tư ${materialId}`)
  const raw = (body as { data: any }).data
  return {
    ...raw,
    // Vật tư chưa có dòng tồn nào: máy chủ có thể không trả các trạng thái tồn; coi là 0 (tránh NaN trên giao diện).
    available: Number(raw.available ?? 0),
    quarantine: Number(raw.quarantine ?? 0),
    blocked: Number(raw.blocked ?? 0),
    damaged: Number(raw.damaged ?? 0),
    rework: Number(raw.rework ?? 0),
    incoming_base: Number(raw.incoming_base ?? 0),
    physical_available: raw.physical_available !== undefined ? Number(raw.physical_available) : Number(raw.available ?? 0),
    reserved_base: raw.reserved_base !== undefined ? Number(raw.reserved_base) : 0,
    available_to_reserve: raw.available_to_reserve !== undefined ? Number(raw.available_to_reserve) : Number(raw.available),
    min_qty: raw.min_qty !== undefined ? Number(raw.min_qty) : 0,
    safety_stock: raw.safety_stock !== undefined ? Number(raw.safety_stock) : 0,
    reorder_point: raw.reorder_point !== undefined ? Number(raw.reorder_point) : 0,
    max_qty: raw.max_qty !== undefined && raw.max_qty !== null ? Number(raw.max_qty) : null,
    threshold: raw.threshold !== undefined ? Number(raw.threshold) : 0,
    replenish_policy: raw.replenish_policy || 'MANUAL',
    below_threshold: Boolean(raw.below_threshold),
    out_of_stock: Boolean(raw.out_of_stock),
    movements: Array.isArray(raw.movements) ? raw.movements.map(normalizeMovement) : [],
  }
}

export type ListStockMovementsFilter = {
  material_id?: string
  type?: StockMovementType | string
  stock_status?: StockStatus
  group_code?: string
  date_from?: string
  date_to?: string
  search?: string
  limit?: number
  offset?: number
  include_free_stock?: boolean
}

export async function listStockMovements(filter?: ListStockMovementsFilter): Promise<StockMovementDto[]> {
  const params = new URLSearchParams()
  if (filter?.material_id) params.set('material_id', filter.material_id)
  if (filter?.type) params.set('type', filter.type)
  if (filter?.stock_status) params.set('stock_status', filter.stock_status)
  if (filter?.group_code) params.set('group_code', filter.group_code)
  if (filter?.date_from) params.set('date_from', filter.date_from)
  if (filter?.date_to) params.set('date_to', filter.date_to)
  if (filter?.search) params.set('search', filter.search)
  if (filter?.include_free_stock !== undefined) params.set('include_free_stock', String(filter.include_free_stock))
  if (filter?.limit != null) params.set('limit', String(filter.limit))
  if (filter?.offset != null) params.set('offset', String(filter.offset))

  const qs = params.toString()
  const res = await apiFetch(`/api/stock-movements${qs ? `?${qs}` : ''}`)
  const body = await parseJson<{ ok: boolean; data: any[] } | InventoryApiErrorResponse>(res)
  throwIfError(res, body, 'Không thể tải biến động kho')
  const list = (body as { data: any[] }).data
  return list.map(normalizeMovement)
}

export async function recordStockIssue(
  payload: StockIssuePayload,
): Promise<StockDocumentResponse> {
  const res = await apiFetch('/api/stock-issues', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  })
  const body = await parseJson<{ ok: boolean; data: StockDocumentResponse } | InventoryApiErrorResponse>(res)
  throwIfError(res, body, 'Không thể tạo phiếu xuất kho')
  return (body as { data: StockDocumentResponse }).data
}

export async function recordSupplierReturn(
  payload: SupplierReturnPayload,
): Promise<StockDocumentResponse> {
  const res = await apiFetch('/api/supplier-returns', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  })
  const body = await parseJson<{ ok: boolean; data: StockDocumentResponse } | InventoryApiErrorResponse>(res)
  throwIfError(res, body, 'Không thể tạo phiếu trả hàng NCC')
  return (body as { data: StockDocumentResponse }).data
}

export async function recordCustomerReturn(
  payload: CustomerReturnPayload,
): Promise<StockDocumentResponse> {
  const res = await apiFetch('/api/customer-returns', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  })
  const body = await parseJson<{ ok: boolean; data: StockDocumentResponse } | InventoryApiErrorResponse>(res)
  throwIfError(res, body, 'Không thể ghi nhận khách trả hàng')
  return (body as { data: StockDocumentResponse }).data
}

export async function recordReworkOrder(
  payload: ReworkOrderPayload,
): Promise<StockDocumentResponse> {
  const res = await apiFetch('/api/rework-orders', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  })
  const body = await parseJson<{ ok: boolean; data: StockDocumentResponse } | InventoryApiErrorResponse>(res)
  throwIfError(res, body, 'Không thể tạo lệnh tái chế')
  return (body as { data: StockDocumentResponse }).data
}

export async function recordStatusTransfer(
  payload: StatusTransferPayload,
): Promise<StockDocumentResponse> {
  const res = await apiFetch('/api/stock-status-transfers', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  })
  const body = await parseJson<{ ok: boolean; data: StockDocumentResponse } | InventoryApiErrorResponse>(res)
  throwIfError(res, body, 'Không thể chuyển đổi trạng thái tồn kho')
  return (body as { data: StockDocumentResponse }).data
}

export async function recordStockAdjustment(
  payload: StockAdjustmentPayload,
): Promise<StockDocumentResponse> {
  const res = await apiFetch('/api/stock-adjustments', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  })
  const body = await parseJson<{ ok: boolean; data: StockDocumentResponse } | InventoryApiErrorResponse>(res)
  throwIfError(res, body, 'Không thể điều chỉnh tồn kho')
  return (body as { data: StockDocumentResponse }).data
}

// ─── STOCK ISSUES (EDITABLE & VOIDABLE) ──────────────────────────

export async function listStockIssues(filter?: {
  limit?: number
  offset?: number
}): Promise<StockIssueDto[]> {
  const params = new URLSearchParams()
  if (filter?.limit != null) params.set('limit', String(filter.limit))
  if (filter?.offset != null) params.set('offset', String(filter.offset))
  const qs = params.toString()
  const res = await apiFetch(`/api/stock-issues${qs ? `?${qs}` : ''}`)
  const body = await parseJson<{ ok: boolean; data: StockIssueDto[] } | InventoryApiErrorResponse>(res)
  throwIfError(res, body, 'Không thể tải danh sách phiếu xuất kho')
  return (body as { data: StockIssueDto[] }).data
}

export async function getStockIssue(id: string): Promise<StockIssueDto | null> {
  const res = await apiFetch(`/api/stock-issues/${encodeURIComponent(id)}`)
  if (res.status === 404) return null
  const body = await parseJson<{ ok: boolean; data: StockIssueDto } | InventoryApiErrorResponse>(res)
  throwIfError(res, body, `Không thể tải phiếu xuất kho ${id}`)
  return (body as { data: StockIssueDto }).data
}

export async function patchStockIssue(
  id: string,
  payload: {
    purpose?: string
    note?: string | null
    receiver_name?: string | null
    receiver_department?: string | null
    lines?: Array<{ id: string; qty_base: number; note?: string | null }>
    reason?: string
  },
  reason?: string,
): Promise<StockIssueDto> {
  const finalReason = reason ?? payload.reason
  const res = await apiFetch(`/api/stock-issues/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...payload, reason: finalReason }),
  })
  const body = await parseJson<{ ok: boolean; data: StockIssueDto } | InventoryApiErrorResponse>(res)
  throwIfError(res, body, `Không thể chỉnh sửa phiếu xuất kho ${id}`)
  return (body as { data: StockIssueDto }).data
}

export async function voidStockIssue(
  id: string,
  reason: string,
): Promise<StockIssueDto> {
  const res = await apiFetch(`/api/stock-issues/${encodeURIComponent(id)}/void`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  })
  const body = await parseJson<{ ok: boolean; data: StockIssueDto } | InventoryApiErrorResponse>(res)
  throwIfError(res, body, `Không thể hủy phiếu xuất kho ${id}`)
  return (body as { data: StockIssueDto }).data
}

export async function getStockIssueHistory(id: string): Promise<DocumentChangeLogDto[]> {
  const res = await apiFetch(`/api/stock-issues/${encodeURIComponent(id)}/history`)
  const body = await parseJson<{ ok: boolean; data: DocumentChangeLogDto[] } | InventoryApiErrorResponse>(res)
  throwIfError(res, body, `Không thể tải lịch sử phiếu xuất kho ${id}`)
  return (body as { data: DocumentChangeLogDto[] }).data
}

// ─── SUPPLIER RETURNS (EDITABLE & VOIDABLE) ──────────────────────

export async function listSupplierReturns(filter?: {
  limit?: number
  offset?: number
}): Promise<StockMovementDto[]> {
  return listStockMovements({
    type: 'RETURN_TO_SUPPLIER',
    limit: filter?.limit,
    offset: filter?.offset,
  })
}

export async function getSupplierReturn(id: string): Promise<SupplierReturnDto | null> {
  const res = await apiFetch(`/api/supplier-returns/${encodeURIComponent(id)}`)
  if (res.status === 404) return null
  const body = await parseJson<{ ok: boolean; data: SupplierReturnDto } | InventoryApiErrorResponse>(res)
  throwIfError(res, body, `Không thể tải phiếu trả hàng ${id}`)
  return (body as { data: SupplierReturnDto }).data
}

export async function patchSupplierReturn(
  id: string,
  payload: {
    reason?: string
    note?: string | null
    lines?: Array<{ id: string; qty_base: number; note?: string | null }>
  },
  auditReason?: string,
): Promise<SupplierReturnDto> {
  const res = await apiFetch(`/api/supplier-returns/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...payload, reason: payload.reason || auditReason, auditReason }),
  })
  const body = await parseJson<{ ok: boolean; data: SupplierReturnDto } | InventoryApiErrorResponse>(res)
  throwIfError(res, body, `Không thể chỉnh sửa phiếu trả hàng ${id}`)
  return (body as { data: SupplierReturnDto }).data
}

export async function voidSupplierReturn(
  id: string,
  reason: string,
): Promise<SupplierReturnDto> {
  const res = await apiFetch(`/api/supplier-returns/${encodeURIComponent(id)}/void`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  })
  const body = await parseJson<{ ok: boolean; data: SupplierReturnDto } | InventoryApiErrorResponse>(res)
  throwIfError(res, body, `Không thể hủy phiếu trả hàng ${id}`)
  return (body as { data: SupplierReturnDto }).data
}

export async function getSupplierReturnHistory(id: string): Promise<DocumentChangeLogDto[]> {
  const res = await apiFetch(`/api/supplier-returns/${encodeURIComponent(id)}/history`)
  const body = await parseJson<{ ok: boolean; data: DocumentChangeLogDto[] } | InventoryApiErrorResponse>(res)
  throwIfError(res, body, `Không thể tải lịch sử phiếu trả hàng ${id}`)
  return (body as { data: DocumentChangeLogDto[] }).data
}

// ─── STOCK INBOUNDS (DIRECT RECEIVING) ───────────────────────────

export async function listStockInbounds(filter?: {
  status?: string
  source?: string
}): Promise<StockInboundDto[]> {
  const params = new URLSearchParams()
  if (filter?.status) params.set('status', filter.status)
  if (filter?.source) params.set('source', filter.source)
  const qs = params.toString()
  const res = await apiFetch(`/api/stock-inbounds${qs ? `?${qs}` : ''}`)
  const body = await parseJson<{ ok: boolean; data: StockInboundDto[] } | InventoryApiErrorResponse>(res)
  throwIfError(res, body, 'Không thể tải danh sách phiếu nhập kho trực tiếp')
  return (body as { data: StockInboundDto[] }).data
}

export async function getStockInbound(id: string): Promise<StockInboundDto | null> {
  const res = await apiFetch(`/api/stock-inbounds/${encodeURIComponent(id)}`)
  if (res.status === 404) return null
  const body = await parseJson<{ ok: boolean; data: StockInboundDto } | InventoryApiErrorResponse>(res)
  throwIfError(res, body, `Không thể tải phiếu nhập kho ${id}`)
  return (body as { data: StockInboundDto }).data
}

export async function createStockInbound(
  payload: CreateStockInboundPayload,
): Promise<StockInboundDto> {
  const res = await apiFetch('/api/stock-inbounds', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const body = await parseJson<{ ok: boolean; data: StockInboundDto } | InventoryApiErrorResponse>(res)
  throwIfError(res, body, 'Không thể tạo phiếu nhập kho trực tiếp')
  return (body as { data: StockInboundDto }).data
}

export async function patchStockInbound(
  id: string,
  payload: PatchStockInboundPayload,
): Promise<StockInboundDto> {
  const res = await apiFetch(`/api/stock-inbounds/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const body = await parseJson<{ ok: boolean; data: StockInboundDto } | InventoryApiErrorResponse>(res)
  throwIfError(res, body, `Không thể chỉnh sửa phiếu nhập kho ${id}`)
  return (body as { data: StockInboundDto }).data
}

export async function voidStockInbound(
  id: string,
  reason: string,
): Promise<StockInboundDto> {
  const res = await apiFetch(`/api/stock-inbounds/${encodeURIComponent(id)}/void`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  })
  const body = await parseJson<{ ok: boolean; data: StockInboundDto } | InventoryApiErrorResponse>(res)
  throwIfError(res, body, `Không thể hủy phiếu nhập kho ${id}`)
  return (body as { data: StockInboundDto }).data
}

export async function getStockInboundHistory(id: string): Promise<DocumentChangeLogDto[]> {
  const res = await apiFetch(`/api/stock-inbounds/${encodeURIComponent(id)}/history`)
  const body = await parseJson<{ ok: boolean; data: DocumentChangeLogDto[] } | InventoryApiErrorResponse>(res)
  throwIfError(res, body, `Không thể tải lịch sử phiếu nhập kho ${id}`)
  return (body as { data: DocumentChangeLogDto[] }).data
}

// ─── BATCH POLICY & STOCKTAKING ───────────────────────────────────

export async function updateStockPoliciesBatch(
  items: Array<{ material_id: string; min_qty?: number | null; note?: string }>,
): Promise<{ updated_count: number; items: any[] }> {
  const res = await apiFetch('/api/stock/policies', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(items),
  })
  const body = await parseJson<{ ok: boolean; data: any } | InventoryApiErrorResponse>(res)
  throwIfError(res, body, 'Không thể cập nhật ngưỡng cảnh báo / ghi chú')
  return (body as { data: any }).data
}

export async function recordStockAdjustmentBatch(payload: {
  reason: string
  items: Array<{ material_id: string; counted_qty: number }>
  force_below_reserved?: boolean
}): Promise<{
  batch_code: string
  adjusted_count: number
  items: any[]
}> {
  const res = await apiFetch('/api/stock-adjustments/batch', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const body = await parseJson<{ ok: boolean; data: any } | InventoryApiErrorResponse>(res)
  throwIfError(res, body, 'Không thể tạo phiếu kiểm kê điều chỉnh')
  return (body as { data: any }).data
}

// ─── PRINT LOGGING ─────────────────────────────────────────────────

export async function recordStockIssuePrintLog(id: string): Promise<DocumentChangeLogDto> {
  const res = await apiFetch(`/api/stock-issues/${id}/print-log`, { method: 'POST' })
  const body = await parseJson<{ ok: boolean; data: DocumentChangeLogDto } | InventoryApiErrorResponse>(res)
  throwIfError(res, body, 'Không thể ghi nhận nhật ký in phiếu xuất')
  return (body as { data: DocumentChangeLogDto }).data
}

export async function recordStockInboundPrintLog(id: string): Promise<DocumentChangeLogDto> {
  const res = await apiFetch(`/api/stock-inbounds/${id}/print-log`, { method: 'POST' })
  const body = await parseJson<{ ok: boolean; data: DocumentChangeLogDto } | InventoryApiErrorResponse>(res)
  throwIfError(res, body, 'Không thể ghi nhận nhật ký in phiếu nhập')
  return (body as { data: DocumentChangeLogDto }).data
}

export async function recordReceiptPrintLog(id: string): Promise<DocumentChangeLogDto> {
  const res = await apiFetch(`/api/receipts/${id}/print-log`, { method: 'POST' })
  const body = await parseJson<{ ok: boolean; data: DocumentChangeLogDto } | InventoryApiErrorResponse>(res)
  throwIfError(res, body, 'Không thể ghi nhận nhật ký in phiếu nhận PO')
  return (body as { data: DocumentChangeLogDto }).data
}




export type StocktakeHistoryItem = {
  material_id: string
  name: string
  uom: string
  stock_status: string
  before: number
  after: number
  delta: number
}

export type StocktakeHistoryBatch = {
  key: string
  created_at: string
  created_by: string
  reason: string
  items: StocktakeHistoryItem[]
}

/** Lịch sử các phiếu kiểm kê: từng vật tư tồn trước, tồn sau và chênh lệch. */
export async function listStocktakeHistory(limit = 50): Promise<StocktakeHistoryBatch[]> {
  const res = await apiFetch(`/api/stock-adjustments/history?limit=${encodeURIComponent(String(limit))}`)
  const body = await parseJson<{ ok: boolean; data: StocktakeHistoryBatch[] } | InventoryApiErrorResponse>(res)
  throwIfError(res, body, 'Không thể tải lịch sử kiểm kê')
  return (body as { data: StocktakeHistoryBatch[] }).data
}
